import { useEffect, useState } from "react";
import { TextField, Button, Stack, Typography, CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";

import BaseModal from "@/components/Modals/BaseModal";
import { useAppDispatch, useAppSelector } from "@/hooks/useRedux";
import { setSnackBarMsjSucceded } from "@/store/slices/auth/actions";
import poiService from "@/services/poi";
import { addSelectedPoi } from "@/store/slices/search";
import serviceUser from "@/services/user";

type ClaimBusiness = {
  poi_id: string | undefined;
  email: string;
  name: string;
  title: string;
  phone: string;
  observation: string;
};

interface IPlaceInfoClaimForm {
  isOpen: boolean;
  onClose: () => void;
}

const formSchema = yup.object({
  email: yup.string().email("Invalid email").required("Email is a required field"),
  name: yup.string().required("Name is a required field"),
  title: yup.string().required("Title is a required field"),
  phone: yup.string().required("Phone is a required field"),
  observation: yup.string().optional(),
});

export default function PlaceInfoClaimForm({ isOpen, onClose }: IPlaceInfoClaimForm) {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const { userInformation } = useAppSelector((state) => state.user);
  const { selectedPoi } = useAppSelector((state) => state.search);
  const handleGetAdminUserInfo = async () => {
    const { data } = await serviceUser.getUserAdminInfo(
      encodeURIComponent(userInformation?.email || ""),
    );

    return data.data.items[0];
  };

  const handleSubmit = async (values: ClaimBusiness) => {
    try {
      const { status } = await poiService.setClaimBusiness(values);

      if (status === 201) {
        dispatch(addSelectedPoi({ ...selectedPoi, claimed: true }));
        dispatch(
          setSnackBarMsjSucceded({
            state: true,
            type: "success",
            msj: "Thank you! We'll email you soon",
          }),
        );
      }
      setLoading(false);
      onClose();
    } catch (error: any) {
      dispatch(setSnackBarMsjSucceded({ state: true, type: "error", msj: error.toString() }));
    }
  };

  const formik = useFormik({
    initialValues: {
      poi_id: selectedPoi?.id,
      email: userInformation?.email,
      name: "",
      title: "",
      phone: "",
      observation: "",
    } as ClaimBusiness,
    validationSchema: formSchema,
    onSubmit: async (values: ClaimBusiness) => {
      setLoading(true);
      handleSubmit(values);
    },
  });

  useEffect(() => {
    if (isOpen) {
      const adminUser = handleGetAdminUserInfo();

      adminUser.then((res) => {
        formik.setFieldValue("name", res.name);
        formik.setFieldValue("title", res.title);
        formik.setFieldValue("phone", res.phone);
        formik.setFieldValue("observation", res.observation);
      });
    }
  }, []);

  return (
    <BaseModal open={isOpen} size="xs" title="Claim This Business" onClose={onClose}>
      <Stack component="form" spacing={2} sx={{ px: 3, pb: 2 }} onSubmit={formik.handleSubmit}>
        <Stack spacing={2}>
          <TextField
            disabled
            fullWidth
            label="email"
            name="email"
            value={formik.values.email ?? ""}
          />
          <TextField
            fullWidth
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            label="Name"
            name="name"
            placeholder="Enter your Name"
            value={formik.values.name ?? ""}
            onChange={formik.handleChange}
          />
          <TextField
            fullWidth
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
            label="Title"
            name="title"
            placeholder="Enter your Title"
            value={formik.values.title ?? ""}
            onChange={formik.handleChange}
          />
          <TextField
            fullWidth
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
            label="Phone Number"
            name="phone"
            placeholder="Enter your phone number"
            value={formik.values.phone ?? ""}
            onChange={formik.handleChange}
          />
          <TextField
            fullWidth
            multiline
            error={formik.touched.observation && Boolean(formik.errors.observation)}
            helperText={formik.touched.observation && formik.errors.observation}
            label="Additional Information"
            minRows={4}
            name="observation"
            placeholder="Anything you want the Argo Team to know?"
            value={formik.values.observation ?? ""}
            onChange={formik.handleChange}
          />
        </Stack>
        <Typography color="text.secondary" variant="body1">
          If you have any questions, please contact us at{" "}
          <Typography color="primary" variant="body1">
            support@argonav.io
          </Typography>
        </Typography>

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button
            color="whiteBg"
            sx={{ color: "primary.main" }}
            variant="outlined"
            onClick={onClose}
          >
            Discard
          </Button>
          <Button color="primary" disabled={loading} type="submit" variant="contained">
            {loading ? <CircularProgress size={30} sx={{ color: "white" }} /> : "Submit"}
          </Button>
        </Stack>
      </Stack>
    </BaseModal>
  );
}
